import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Avatar,
  Grid,
  LinearProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  Card,
  CardContent,
  TextField,
  Fab,
  Paper,
  Stack,
  Tooltip,
  Fade,
  Zoom,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  Comment as CommentIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  AccessTime as AccessTimeIcon,
  Flag as FlagIcon,
  Label as LabelIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';

// RTK Query hooks
import {
  useGetTaskQuery,
  useUpdateTaskMutation,
  useAddCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
} from '../../../services/taskApi';

// Socket service
import socketService from '../../../services/socketService';

// Redux store and taskApi
import { store } from '../../../store/store';
import { taskApi } from '../../../services/taskApi';

const TaskDetail = ({ open, taskId, onClose, onEdit, onDelete, onStatusUpdate }) => {
  // RTK Query hooks
  const { data: taskData, isLoading: taskLoading, error: taskError } = useGetTaskQuery(taskId, {
    skip: !taskId || !open,
    refetchOnMountOrArgChange: true
  });
  const [updateTask] = useUpdateTaskMutation();
  const [addComment] = useAddCommentMutation();
  const [updateComment] = useUpdateCommentMutation();
  const [deleteComment] = useDeleteCommentMutation();

  const [taskMenuAnchorEl, setTaskMenuAnchorEl] = useState(null);
  const [commentMenuAnchorEl, setCommentMenuAnchorEl] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [commentModal, setCommentModal] = useState({
    open: false,
    mode: 'edit', // 'edit' or 'delete'
    comment: null
  });
  const [editCommentText, setEditCommentText] = useState('');
  const [customProgress, setCustomProgress] = useState(0);

  // Extract task from RTK Query data
  const task = taskData?.data?.task;

  // Update customProgress when task data changes
  React.useEffect(() => {
    if (task?.progress !== undefined) {
      setCustomProgress(task.progress);
    }
  }, [task?.progress]);

  // Socket event listeners for real-time comment updates
  useEffect(() => {
    if (!open || !taskId) return;

    const handleCommentAdded = (data) => {
      console.log('📨 TaskDetail received task:comment-added event:', data);
      if (data.taskId === taskId) {
        // Invalidate task cache to trigger refetch
        console.log('🔄 Invalidating task cache for new comment');
        store.dispatch(taskApi.util.invalidateTags([{ type: 'Task', id: taskId }]));
      }
    };

    const handleCommentUpdated = (data) => {
      console.log('📨 TaskDetail received task:comment-updated event:', data);
      if (data.taskId === taskId) {
        console.log('🔄 Invalidating task cache for updated comment');
        store.dispatch(taskApi.util.invalidateTags([{ type: 'Task', id: taskId }]));
      }
    };

    const handleCommentDeleted = (data) => {
      console.log('📨 TaskDetail received task:comment-deleted event:', data);
      if (data.taskId === taskId) {
        console.log('🔄 Invalidating task cache for deleted comment');
        store.dispatch(taskApi.util.invalidateTags([{ type: 'Task', id: taskId }]));
      }
    };

    // Add event listeners
    socketService.socket?.on('task:comment-added', handleCommentAdded);
    socketService.socket?.on('task:comment-updated', handleCommentUpdated);
    socketService.socket?.on('task:comment-deleted', handleCommentDeleted);

    // Cleanup function
    return () => {
      socketService.socket?.off('task:comment-added', handleCommentAdded);
      socketService.socket?.off('task:comment-updated', handleCommentUpdated);
      socketService.socket?.off('task:comment-deleted', handleCommentDeleted);
    };
  }, [open, taskId]);

  if (!open) return null;
  if (taskLoading) {
    return (
      <Dialog open={open} maxWidth="lg" fullWidth>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{
              width: 40,
              height: 40,
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #1976d2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              mx: 'auto',
              mb: 2
            }} />
            <Typography>Loading task details...</Typography>
          </Box>
        </Box>
      </Dialog>
    );
  }
  if (taskError || !task) {
    return (
      <Dialog open={open} maxWidth="lg" fullWidth>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="error" variant="h6" gutterBottom>
            Failed to load task
          </Typography>
          <Typography color="text.secondary">
            {taskError?.data?.message || 'Task not found'}
          </Typography>
          <Button onClick={onClose} sx={{ mt: 2 }}>
            Close
          </Button>
        </Box>
      </Dialog>
    );
  }

  const handleTaskMenuOpen = (event) => {
    setTaskMenuAnchorEl(event.currentTarget);
  };

  const handleTaskMenuClose = () => {
    setTaskMenuAnchorEl(null);
  };

  const handleCommentMenuOpen = (event) => {
    setCommentMenuAnchorEl(event.currentTarget);
  };

  const handleCommentMenuClose = () => {
    setCommentMenuAnchorEl(null);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setLoading(true);
      const updates = { status: newStatus };

      // Auto-set progress based on status changes
      switch (newStatus) {
        case 'pending':
          updates.progress = 0;
          break;
        case 'in_progress':
          // If reopening from completed/cancelled, set to 25%, otherwise keep current progress
          if (task.status === 'completed' || task.status === 'cancelled') {
            updates.progress = 25;
          }
          break;
        case 'on_hold':
          // Keep current progress when putting on hold
          break;
        case 'completed':
          updates.progress = 100;
          break;
        case 'cancelled':
          updates.progress = 0;
          break;
        default:
          break;
      }

      await updateTask({ taskId: task._id, updates }).unwrap();
      onStatusUpdate(task, newStatus);
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;

    try {
      await addComment({
        taskId: task._id,
        comment: commentText.trim()
      }).unwrap();
      setCommentText('');
      showNotification('Comment added successfully', 'success');

      // Auto-scroll to bottom after adding comment
      setTimeout(() => {
        const commentsContainer = document.querySelector('[data-comments-container]');
        if (commentsContainer) {
          commentsContainer.scrollTop = commentsContainer.scrollHeight;
        }
      }, 100);
    } catch (error) {
      console.error('Failed to add comment:', error);
      showNotification('Failed to add comment', 'error');
    }
  };

  const handleOpenCommentModal = (mode, comment) => {
    setCommentModal({
      open: true,
      mode,
      comment
    });
    if (mode === 'edit' && comment) {
      setEditCommentText(comment.message);
    }
  };

  const handleCloseCommentModal = () => {
    setCommentModal({
      open: false,
      mode: 'edit',
      comment: null
    });
    setEditCommentText('');
  };

  const handleEditComment = async (commentId, message) => {
    if (!message.trim()) return;

    try {
      setIsSubmitting(true);
      await updateComment({
        taskId: task._id,
        commentId,
        message: message.trim()
      }).unwrap();
      handleCloseCommentModal();
      showNotification('Comment updated successfully', 'success');
    } catch (error) {
      console.error('Failed to update comment:', error);
      showNotification('Failed to update comment', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      setIsSubmitting(true);
      await deleteComment({
        taskId: task._id,
        commentId
      }).unwrap();
      handleCloseCommentModal();
      showNotification('Comment deleted successfully', 'success');
    } catch (error) {
      console.error('Failed to delete comment:', error);
      showNotification('Failed to delete comment', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({ message, severity });
    setTimeout(() => setNotification(null), 3000);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return '#d32f2f';
      case 'high': return '#f57c00';
      case 'medium': return '#1976d2';
      case 'low': return '#388e3c';
      default: return '#757575';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#4caf50';
      case 'in_progress': return '#2196f3';
      case 'on_hold': return '#ff9800';
      case 'pending': return '#9e9e9e';
      case 'cancelled': return '#f44336';
      default: return '#757575';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleProgressUpdate = async (newProgress) => {
    try {
      setLoading(true);
      await updateTask({ taskId: task._id, updates: { progress: newProgress } }).unwrap();
      showNotification(`Progress updated to ${newProgress}%`, 'success');
    } catch (error) {
      console.error('Failed to update progress:', error);
      showNotification('Failed to update progress', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusActions = () => {
    const actions = [];
    if (task.status === 'pending') {
      actions.push({ label: 'Start Task', status: 'in_progress', icon: PlayArrowIcon });
    } else if (task.status === 'in_progress') {
      actions.push({ label: 'Mark Complete', status: 'completed', icon: CheckCircleIcon });
      actions.push({ label: 'Put On Hold', status: 'on_hold', icon: PauseIcon });
    } else if (task.status === 'on_hold') {
      actions.push({ label: 'Resume Task', status: 'in_progress', icon: PlayArrowIcon });
    } else if (task.status === 'completed') {
      actions.push({ label: 'Reopen Task', status: 'in_progress', icon: PlayArrowIcon });
    }
    return actions;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      TransitionComponent={Fade}
      transitionDuration={300}
      PaperProps={{
        sx: {
          borderRadius: 4,
          maxHeight: '95vh',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)'
        }
      }}
    >
      {/* Modern Header */}
      <Box sx={{
        p: 4,
        background: `linear-gradient(135deg, ${getStatusColor(task.status)}10 0%, ${getStatusColor(task.status)}05 100%)`,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                mb: 2,
                background: `linear-gradient(135deg, ${getStatusColor(task.status)} 0%, ${getStatusColor(task.status)}dd 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                lineHeight: 1.2
              }}
            >
              {task.title}
            </Typography>

            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Chip
                icon={<FlagIcon />}
                size="small"
                label={task.priority.toUpperCase()}
                sx={{
                  backgroundColor: `${getPriorityColor(task.priority)}15`,
                  color: getPriorityColor(task.priority),
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  border: `1px solid ${getPriorityColor(task.priority)}30`,
                  '& .MuiChip-icon': { fontSize: '1rem' }
                }}
              />
              <Chip
                icon={<TimelineIcon />}
                size="small"
                label={task.status.replace('_', ' ').toUpperCase()}
                sx={{
                  backgroundColor: `${getStatusColor(task.status)}15`,
                  color: getStatusColor(task.status),
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  border: `1px solid ${getStatusColor(task.status)}30`,
                  '& .MuiChip-icon': { fontSize: '1rem' }
                }}
              />
              {task.category && (
                <Chip
                  icon={<LabelIcon />}
                  size="small"
                  label={task.category}
                  variant="outlined"
                  sx={{
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    fontWeight: 600,
                    '& .MuiChip-icon': { fontSize: '1rem' }
                  }}
                />
              )}
              {task.isOverdue && (
                <Chip
                  icon={<AccessTimeIcon />}
                  size="small"
                  label="OVERDUE"
                  sx={{
                    backgroundColor: 'error.main',
                    color: 'white',
                    fontWeight: 800,
                    fontSize: '0.75rem',
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.7 },
                      '100%': { opacity: 1 }
                    }
                  }}
                />
              )}
            </Stack>

            {/* Progress Preview */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1, maxWidth: 200 }}>
                <LinearProgress
                  variant="determinate"
                  value={task.progress}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                      background: `linear-gradient(90deg, ${getStatusColor(task.status)} 0%, ${getStatusColor(task.status)}dd 100%)`
                    }
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                {task.progress}% Complete
              </Typography>
            </Box>
          </Box>

          <Tooltip title="More actions">
            <IconButton
              onClick={handleTaskMenuOpen}
              size="large"
              sx={{
                ml: 2,
                backgroundColor: 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <DialogContent sx={{ px: 4, py: 3 }}>
        {/* Modern Quick Actions */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: 'text.primary' }}>
            Quick Actions
          </Typography>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {getStatusActions().map((action, index) => {
              const IconComponent = action.icon;
              return (
                <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Button
                    key={action.status}
                    variant="contained"
                    size="medium"
                    startIcon={<IconComponent />}
                    onClick={() => handleStatusChange(action.status)}
                    disabled={loading}
                    sx={{
                      borderRadius: 3,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      py: 1.5,
                      background: `linear-gradient(135deg, ${getStatusColor(action.status)} 0%, ${getStatusColor(action.status)}dd 100%)`,
                      boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                        background: `linear-gradient(135deg, ${getStatusColor(action.status)}dd 0%, ${getStatusColor(action.status)} 100%)`,
                      },
                      '&:disabled': {
                        opacity: 0.6,
                        transform: 'none'
                      }
                    }}
                  >
                    {action.label}
                  </Button>
                </Zoom>
              );
            })}
          </Stack>
        </Paper>

        <Grid container spacing={3}>
          {/* Left Column */}
          <Grid item xs={12} md={8}>
            {/* Description */}
            {task.description && (
              <Card
                sx={{
                  mb: 3,
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CommentIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      Description
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.7,
                      color: 'text.secondary',
                      fontSize: '1rem'
                    }}
                  >
                    {task.description}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Progress */}
            <Card
              sx={{
                mb: 3,
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <TimelineIcon sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      Progress
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                      Track your task completion
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: getStatusColor(task.status) }}>
                      {task.progress}%
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Complete
                    </Typography>
                  </Box>
                </Box>

                {/* Progress Bar */}
                <Box sx={{ position: 'relative', mb: 3 }}>
                  <LinearProgress
                    variant="determinate"
                    value={task.progress}
                    sx={{
                      height: 12,
                      borderRadius: 6,
                      backgroundColor: 'rgba(255,255,255,0.3)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 6,
                        background: `linear-gradient(90deg, ${getStatusColor(task.status)} 0%, ${getStatusColor(task.status)}dd 100%)`,
                        boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
                      }
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -8,
                      left: `${task.progress}%`,
                      transform: 'translateX(-50%)',
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: getStatusColor(task.status),
                      border: '3px solid white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                    }}
                  />
                </Box>

                {/* Progress Milestones */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
                    Quick Progress Updates
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {[25, 50, 75, 100].map((milestone) => (
                      <Button
                        key={milestone}
                        variant={task.progress === milestone ? "contained" : "outlined"}
                        size="small"
                        onClick={() => handleProgressUpdate(milestone)}
                        disabled={loading}
                        sx={{
                          minWidth: 60,
                          borderRadius: 2,
                          fontWeight: 600,
                          textTransform: 'none',
                          borderColor: getStatusColor(task.status),
                          color: task.progress === milestone ? 'white' : getStatusColor(task.status),
                          backgroundColor: task.progress === milestone ? getStatusColor(task.status) : 'transparent',
                          '&:hover': {
                            backgroundColor: task.progress === milestone ? getStatusColor(task.status) : `${getStatusColor(task.status)}10`,
                            borderColor: getStatusColor(task.status),
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        {milestone}%
                      </Button>
                    ))}
                  </Stack>
                </Box>

                {/* Manual Progress Input */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    type="number"
                    size="small"
                    label="Custom Progress"
                    value={customProgress}
                    onChange={(e) => setCustomProgress(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleProgressUpdate(customProgress);
                      }
                    }}
                    inputProps={{ min: 0, max: 100 }}
                    sx={{
                      width: 140,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'white',
                        '& fieldset': { borderColor: 'grey.300' },
                        '&:hover fieldset': { borderColor: 'primary.main' },
                        '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleProgressUpdate(customProgress)}
                    disabled={loading || customProgress === task.progress}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: 'none',
                      backgroundColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      },
                      '&:disabled': {
                        backgroundColor: 'grey.300',
                        color: 'grey.500',
                        transform: 'none'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    Update
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Modern Comments Section */}
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {/* Header with Stats */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CommentIcon sx={{ color: 'white', fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.1rem' }}>
                        Discussion
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {task.comments?.length || 0} comment{task.comments?.length !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Notification Toast */}
                  {notification && (
                    <Fade in={true}>
                      <Paper
                        sx={{
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          backgroundColor: notification.severity === 'success' ? 'success.light' : 'error.light',
                          color: notification.severity === 'success' ? 'success.dark' : 'error.dark',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                      >
                        {notification.message}
                      </Paper>
                    </Fade>
                  )}
                </Box>

                {/* Compact Add Comment */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 3,
                    borderRadius: 2,
                    backgroundColor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s ease-in-out',
                    '&:focus-within': {
                      backgroundColor: 'white',
                      borderColor: 'primary.main',
                      boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                      U
                    </Avatar>
                    <Box sx={{ flex: 1, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Add a comment..."
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleAddComment();
                          }
                        }}
                        multiline
                        maxRows={3}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: 'white',
                            fontSize: '0.875rem',
                            '& fieldset': { border: 'none' },
                            '&:hover fieldset': { border: 'none' },
                            '&.Mui-focused fieldset': { border: 'none' }
                          }
                        }}
                      />
                      <Tooltip title="Post comment">
                        <IconButton
                          onClick={handleAddComment}
                          disabled={!commentText.trim()}
                          size="small"
                          sx={{
                            width: 36,
                            height: 36,
                            background: `linear-gradient(135deg, ${getStatusColor(task.status)} 0%, ${getStatusColor(task.status)}dd 100%)`,
                            color: 'white',
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'scale(1.05)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            },
                            '&:disabled': {
                              backgroundColor: 'grey.300',
                              color: 'grey.500',
                              transform: 'none',
                              boxShadow: 'none'
                            }
                          }}
                        >
                          <SendIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Paper>

                {/* Compact Comments List */}
                <Box
                  data-comments-container
                  sx={{
                    maxHeight: 500,
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'grey.100',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'grey.400',
                      borderRadius: '3px',
                      '&:hover': {
                        backgroundColor: 'grey.500',
                      },
                    },
                  }}
                >
                  {task.comments && task.comments.length > 0 ? (
                    <Stack spacing={2}>
                      {task.comments.map((comment, index) => (
                        <Fade in={true} style={{ transitionDelay: `${index * 50}ms` }}>
                          <Paper
                            key={comment._id || index}
                            elevation={0}
                            sx={{
                              p: 2.5,
                              borderRadius: 2,
                              backgroundColor: 'grey.50',
                              border: '1px solid',
                              borderColor: 'divider',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                backgroundColor: 'white',
                                borderColor: 'primary.light',
                                transform: 'translateX(2px)'
                              }
                            }}
                          >
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Avatar
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      bgcolor: `hsl(${index * 137.5 % 360}, 70%, 50%)`,
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    {comment.userId?.firstName?.charAt(0)}{comment.userId?.lastName?.charAt(0)}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                      {comment.userId?.firstName} {comment.userId?.lastName}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                      {formatDate(comment.createdAt)} • {new Date(comment.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </Typography>
                                  </Box>
                                </Box>

                                {/* Comment Actions Menu */}
                                <Menu
                                  anchorEl={commentMenuAnchorEl}
                                  open={Boolean(commentMenuAnchorEl)}
                                  onClose={handleCommentMenuClose}
                                  PaperProps={{
                                    sx: { borderRadius: 2, minWidth: 120 }
                                  }}
                                >
                                  <MenuItem onClick={() => { handleOpenCommentModal('edit', comment); handleCommentMenuClose(); }}>
                                    <EditIcon sx={{ mr: 2, fontSize: 16 }} />
                                    Edit Comment
                                  </MenuItem>
                                  <Divider />
                                  <MenuItem
                                    onClick={() => { handleOpenCommentModal('delete', comment); handleCommentMenuClose(); }}
                                    sx={{ color: 'error.main' }}
                                  >
                                    <DeleteIcon sx={{ mr: 2, fontSize: 16 }} />
                                    Delete Comment
                                  </MenuItem>
                                </Menu>

                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCommentMenuOpen(e);
                                  }}
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    opacity: 0.6,
                                    '&:hover': { opacity: 1 }
                                  }}
                                >
                                  <MoreVertIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>

                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'text.primary',
                                  lineHeight: 1.5,
                                  whiteSpace: 'pre-wrap',
                                  fontSize: '0.875rem'
                                }}
                              >
                                {comment.message}
                              </Typography>
                            </Box>
                          </Paper>
                        </Fade>
                      ))}
                    </Stack>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <CommentIcon sx={{ fontSize: 40, color: 'grey.300', mb: 2 }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        No comments yet
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Start the conversation above
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} md={4}>
            {/* Task Details */}
            <Card
              sx={{
                mb: 3,
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(135deg, #ffffff 0%, #fefefe 100%)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <PersonIcon sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      Task Details
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Key information and metadata
                    </Typography>
                  </Box>
                </Box>

                <Stack spacing={3}>
                  {/* Dates */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      backgroundColor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <CalendarIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Timeline
                      </Typography>
                    </Box>

                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          START DATE
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {formatDate(task.startDate)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          DUE DATE
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: task.isOverdue ? 'error.main' : 'text.primary'
                          }}
                        >
                          {formatDate(task.dueDate)}
                          {task.isOverdue && (
                            <Typography component="span" sx={{ color: 'error.main', fontSize: '0.75rem', ml: 1 }}>
                              ({Math.abs(task.daysRemaining)} days overdue)
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>

                  {/* Assigned Users */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      backgroundColor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Assigned Team ({task.assignedToUsers?.length || 0})
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {task.assignedToUsers?.map((user, index) => (
                        <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }}>
                          <Chip
                            key={user._id}
                            label={`${user.firstName} ${user.lastName}`}
                            size="small"
                            avatar={
                              <Avatar
                                sx={{
                                  width: 24,
                                  height: 24,
                                  bgcolor: `hsl(${index * 137.5 % 360}, 70%, 50%)`
                                }}
                              >
                                {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                              </Avatar>
                            }
                            sx={{
                              borderRadius: 2,
                              fontWeight: 600,
                              backgroundColor: 'white',
                              border: '1px solid',
                              borderColor: 'divider',
                              '&:hover': {
                                backgroundColor: 'primary.light',
                                color: 'white'
                              }
                            }}
                          />
                        </Zoom>
                      ))}
                    </Box>
                  </Paper>

                  {/* Tags */}
                  {task.tags && task.tags.length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        backgroundColor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <LabelIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Tags
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {task.tags.map((tag, index) => (
                          <Fade in={true} style={{ transitionDelay: `${index * 50}ms` }}>
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderRadius: 2,
                                fontWeight: 500,
                                borderColor: 'primary.light',
                                color: 'primary.main',
                                '&:hover': {
                                  backgroundColor: 'primary.light',
                                  color: 'white'
                                }
                              }}
                            />
                          </Fade>
                        ))}
                      </Box>
                    </Paper>
                  )}

                  {/* Estimated Hours */}
                  {task.estimatedHours > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        backgroundColor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <AccessTimeIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Time Estimate
                        </Typography>
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {task.estimatedHours} hours
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Estimated completion time
                      </Typography>
                    </Paper>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Modern Overdue Alert */}
            {task.isOverdue && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(244, 67, 54, 0.1)',
                  border: '1px solid',
                  borderColor: 'error.light',
                  background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
                  '& .MuiAlert-icon': {
                    color: 'error.main'
                  }
                }}
                icon={<AccessTimeIcon />}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  ⚠️ Task Overdue
                </Typography>
                <Typography variant="body2">
                  This task was due on {formatDate(task.dueDate)} and is {Math.abs(task.daysRemaining)} days past due.
                </Typography>
              </Alert>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: 2, px: 3 }}
        >
          Close
        </Button>
      </DialogActions>

      {/* Task Context Menu */}
      <Menu
        anchorEl={taskMenuAnchorEl}
        open={Boolean(taskMenuAnchorEl)}
        onClose={handleTaskMenuClose}
        PaperProps={{
          sx: { borderRadius: 2, minWidth: 150 }
        }}
      >
        <MenuItem onClick={() => { onEdit(task); onClose(); }}>
          <EditIcon sx={{ mr: 2, fontSize: 18 }} />
          Edit Task
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => { onDelete(task); onClose(); }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 2, fontSize: 18 }} />
          Delete Task
        </MenuItem>
      </Menu>

      {/* Comment Edit Modal */}
      <Dialog
        open={commentModal.open && commentModal.mode === 'edit'}
        onClose={handleCloseCommentModal}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={400}
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 32px 64px rgba(0,0,0,0.2)',
            background: 'linear-gradient(145deg, #ffffff 0%, #fafbfc 100%)',
            border: '1px solid',
            borderColor: 'rgba(0,0,0,0.08)',
            overflow: 'hidden'
          }
        }}
      >
        {/* Modern Header */}
        <Box sx={{
          p: 4,
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
          color: 'white',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.1
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, position: 'relative', zIndex: 1 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.3)'
              }}
            >
              <EditIcon sx={{ fontSize: 28, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, lineHeight: 1.2 }}>
                Edit Comment
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                Update your comment content
              </Typography>
            </Box>
          </Box>
        </Box>

        <DialogContent sx={{ p: 4, pt: 3 }}>
          {/* Original Comment Preview */}
          {commentModal.comment && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CommentIcon sx={{ fontSize: 18 }} />
                Original Comment
              </Typography>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  position: 'relative'
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.6 }}>
                  "{commentModal.comment.message}"
                </Typography>
              </Paper>
            </Box>
          )}

          {/* Edit Form */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <EditIcon sx={{ fontSize: 18 }} />
              New Comment
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={5}
              value={editCommentText}
              onChange={(e) => setEditCommentText(e.target.value)}
              placeholder="Enter your updated comment..."
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: 'grey.50',
                  transition: 'all 0.3s ease',
                  '& fieldset': {
                    borderColor: 'grey.300',
                    borderWidth: 2
                  },
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 3,
                    boxShadow: '0 0 0 4px rgba(99, 102, 241, 0.1)'
                  }
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '0.95rem',
                  lineHeight: 1.6,
                  padding: '16px 20px'
                }
              }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
              {editCommentText.length} characters
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          px: 4,
          pb: 4,
          pt: 2,
          gap: 2,
          borderTop: '1px solid',
          borderColor: 'grey.100',
          backgroundColor: 'grey.50'
        }}>
          <Button
            onClick={handleCloseCommentModal}
            variant="outlined"
            size="large"
            sx={{
              borderRadius: 3,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              borderColor: 'grey.300',
              color: 'text.secondary',
              '&:hover': {
                borderColor: 'grey.400',
                backgroundColor: 'grey.100'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (editCommentText.trim() && commentModal.comment) {
                handleEditComment(commentModal.comment._id, editCommentText.trim());
              }
            }}
            variant="contained"
            size="large"
            disabled={isSubmitting || !editCommentText.trim()}
            sx={{
              borderRadius: 3,
              px: 4,
              py: 1.5,
              fontWeight: 700,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              boxShadow: '0 8px 25px rgba(99, 102, 241, 0.3)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)',
                boxShadow: '0 12px 35px rgba(99, 102, 241, 0.4)',
                transform: 'translateY(-2px)'
              },
              '&:disabled': {
                background: 'grey.300',
                color: 'grey.500',
                boxShadow: 'none',
                transform: 'none'
              }
            }}
          >
            {isSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 16,
                  height: 16,
                  border: '2px solid transparent',
                  borderTop: '2px solid currentColor',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Updating...
              </Box>
            ) : (
              'Update Comment'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comment Delete Modal */}
      {commentModal.open && commentModal.mode === 'delete' && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-900 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <DeleteIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Delete Comment
                    </h3>
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete this comment?
                    </p>
                  </div>
                </div>

                {commentModal.comment && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-700 italic">
                      "{commentModal.comment.message}"
                    </p>
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleCloseCommentModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (commentModal.comment) {
                        handleDeleteComment(commentModal.comment._id);
                      }
                    }}
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default TaskDetail;
